/**
 * V17.516 (Phase 0, brief 2026-10-25-003 step 8): a real 403.
 *
 * ProtectedRoute renders this IN PLACE when a route refuses the user — the URL
 * stays the one they asked for, and the missing permission code(s) are shown,
 * so "I clicked X and landed on Y with no message" (R8 / S7) cannot happen.
 * Also routed at /403. Lao by default (LanguageContext), English on toggle.
 */
import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageContext } from '../contexts/LanguageContext';
import { usePermissions } from '../hooks/usePermissions';
import { firstOpenPage } from '../components/common/noAccessLandings';

const TEXT = {
  lo: {
    title: 'ທ່ານບໍ່ມີສິດເຂົ້າເຖິງໜ້ານີ້',
    body: 'ບັນຊີຂອງທ່ານບໍ່ມີສິດທີ່ໜ້ານີ້ຕ້ອງການ. ຖ້າທ່ານຕ້ອງການເຂົ້າ, ກະລຸນາຕິດຕໍ່ຜູ້ດູແລລະບົບ.',
    needs: 'ສິດທີ່ຕ້ອງການ',
    needsAny: 'ຕ້ອງມີຢ່າງໃດຢ່າງໜຶ່ງ',
    back: 'ກັບໄປໜ້າຫຼັກຂອງທ່ານ',
    logout: 'ອອກຈາກລະບົບ',
    role: 'ໜ້ານີ້ສະເພາະບາງບົດບາດເທົ່ານັ້ນ',
  },
  en: {
    title: 'You do not have access to this page',
    body: 'Your account does not hold the permission this page needs. Ask your administrator if you need it.',
    needs: 'Permission needed',
    needsAny: 'Any one of',
    back: 'Back to your home page',
    logout: 'Log out',
    role: 'This page is for certain roles only',
  },
};

interface ForbiddenProps {
  /** Every listed code is required (requirePermission / requireAllPermissions). */
  required?: string[];
  /** Any one listed code is enough (requireAnyPermission / requireAnyOf). */
  anyOf?: string[];
  /** A role-based refusal (deprecated role props, role assignment, scout exclusion). */
  roleBased?: boolean;
}

export default function Forbidden({ required = [], anyOf = [], roleBased = false }: ForbiddenProps) {
  const language = useContext(LanguageContext)?.language ?? 'lo';
  const t = TEXT[language];
  const { getDefaultRoute, logout } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const { pathname } = useLocation();
  // Row 43 kept: when the user's default page IS this page, link to the first page they can
  // open instead (never a link back to the refusal); with none, offer logout.
  const home = getDefaultRoute();
  const back = home.split('?')[0] === pathname ? firstOpenPage(hasAnyPermission, pathname) : home;
  const codes = required.length ? required : anyOf;
  return (
    <div data-testid="forbidden-page" lang={language} className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-lg text-center">
        <p className="text-sm font-semibold text-red-600 dark:text-red-400">403</p>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{t.title}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t.body}</p>
        {codes.length > 0 && (
          <div className="mt-4 rounded-md bg-gray-100 dark:bg-gray-800 p-3 text-left" data-testid="forbidden-codes">
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {required.length ? t.needs : t.needsAny}
            </p>
            <ul className="mt-1 space-y-0.5">
              {codes.map((c) => (
                <li key={c}>
                  <code className="font-mono text-sm text-gray-900 dark:text-gray-100">{c}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
        {roleBased && codes.length === 0 && (
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">{t.role}</p>
        )}
        <div className="mt-6">
          {back ? (
            <Link
              to={back}
              className="inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t.back}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => logout?.()}
              className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              {t.logout}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
